import { LitElement, html, css } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { consume } from "@lit/context";
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartDataset,
  type ChartOptions,
} from "chart.js";

export interface RadialScaleConfig {
  min: number;
  max: number;
  ticks: { stepSize: number };
  grid: { color: string };
  angleLines: { color: string };
  pointLabels: { color: string; font: { size: number } };
}
import type { RatingConfig } from "../models";
import { withOpacity } from "../utils/color";
import { themeContext, configContext } from "../contexts";
import { themeStyles } from "../styles/themes";
import { DATASET_COLORS } from "../constants";
import C2S from "canvas2svg";

Chart.register(
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

const POINT_STYLES = [
  "circle",
  "triangle",
  "rect",
  "star",
  "cross",
  "rectRot",
  "crossRot",
];

/**
 * Renders a Chart.js radar (spider) chart from a `RatingConfig` and `AppState`.
 * The chart is initialised on first render and updated reactively whenever
 * `config`, `state`, or `theme` changes. Updates are throttled to ensure
 * smooth performance. The Chart instance is destroyed on disconnect.
 */
@customElement("bab-spider-chart")
export class BabSpiderChart extends LitElement {
  static styles = [
    themeStyles,
    css`
    :host {
      display: block;
      width: 100%;
      position: relative;
    }
    canvas {
      width: 100% !important;
      height: 100% !important;
      display: block;
    }
    .watermark {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--bab-text-secondary);
      opacity: 0.5;
      pointer-events: none;
      text-align: center;
      width: 100%;
      padding: 0 40px;
      box-sizing: border-box;
    }
    .chart-title {
      text-align: center;
      margin-bottom: 12px;
      font-weight: 700;
      font-size: 1.1rem;
      color: var(--bab-text);
      min-height: 1.5rem;
    }
    .expand-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: 8px;
      color: var(--bab-text-secondary);
      cursor: pointer;
      transition: all 0.2s;
      z-index: 10;
      padding: 0;
    }
    .expand-btn:hover {
      color: var(--bab-accent);
      border-color: var(--bab-accent);
      background: var(--bab-bg);
      transform: scale(1.05);
    }
    .expand-btn svg {
      width: 18px;
      height: 18px;
    }
    .overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.85);
      z-index: 5000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
      box-sizing: border-box;
      backdrop-filter: blur(8px);
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
    }
    :host([expanded]) {
      z-index: 5000;
    }
    .overlay--visible {
      opacity: 1;
      visibility: visible;
    }
    .overlay-content {
      width: 100%;
      max-width: 900px;
      height: 100%;
      max-height: 900px;
      background: var(--bab-bg);
      border-radius: var(--bab-radius, 16px);
      padding: 24px;
      box-sizing: border-box;
      position: relative;
      display: flex;
      flex-direction: column;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
      opacity: 1;
    }
    .overlay .chart-title {
      font-size: 1.5rem;
      margin-bottom: 24px;
    }
    .close-btn {
      position: absolute;
      top: 16px;
      right: 16px;
      background: transparent;
      border: none;
      color: var(--bab-text-secondary);
      cursor: pointer;
      padding: 8px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }
    .close-btn:hover {
      background: rgba(0, 0, 0, 0.05);
      color: var(--bab-text);
      transform: rotate(90deg);
    }
    .overlay canvas {
      flex: 1;
      min-height: 0;
      height: auto !important;
    }
    .chart-container {
      position: relative;
      width: 100%;
      aspect-ratio: 1;
      margin: 0 auto;
    }
  `];

  @consume({ context: configContext, subscribe: true })
  @state()
  config!: RatingConfig;

  /** List of rating sets to display. */
  @property({ type: Array })
  datasets: import("../models").RatingSet[] = [];

  /**
   * The version key whose attributeIds and scale should be used for the chart.
   * Falls back to `config.currentVersion` when not set.
   */
  @property({ type: String })
  activeVersion: string = "";

  @consume({ context: themeContext, subscribe: true })
  @state()
  theme: import("../models").Theme = "system";

  @query("canvas")
  private _canvas!: HTMLCanvasElement;

  @query(".overlay canvas")
  private _overlayCanvas!: HTMLCanvasElement;

  @state() private _isEmpty = true;
  @state() private _isExpanded = false;

  private _chart: Chart | null = null;
  private _overlayChart: Chart | null = null;
  private _updateRequested = false;

  private _onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this._isExpanded) {
      this._toggleExpand();
    }
  };

  private _getChartTitle(): string {
    const namedDatasets = this.datasets
      .map(d => d.name?.trim())
      .filter(Boolean);
    
    if (namedDatasets.length === 0) return "";
    return namedDatasets.join(" vs ");
  }

  private _activeVersionKey(): string {
    return (this.activeVersion && this.config.versions[this.activeVersion])
      ? this.activeVersion
      : this.config.currentVersion;
  }

  private _versionAttributes() {
    const ids = this.config.versions[this._activeVersionKey()].attributeIds;
    const attrMap = new Map(this.config.attributes.map((a) => [a.id, a]));
    return ids.map((id) => attrMap.get(id)).filter((a) => a !== undefined);
  }

  private _labels(): string[] {
    return this._versionAttributes().map((a) => a.name);
  }

  private _datasets(): ChartDataset<"radar">[] {
    const attrs = this._versionAttributes();
    return this.datasets.map((set, i) => {
      const color = set.color || DATASET_COLORS[i % DATASET_COLORS.length];
      const pointStyle = POINT_STYLES[i % POINT_STYLES.length];
      return {
        label: set.name || `${i + 1}`,
        data: attrs.map((a) => set.ratings[a.id] ?? null),
        backgroundColor: withOpacity(color, 0.2),
        borderColor: color,
        borderWidth: 2,
        pointBackgroundColor: color,
        pointStyle,
        pointRadius: 5,
        pointHoverRadius: 7,
      } as ChartDataset<"radar">;
    });
  }

  private _getThemeValue(name: string): string {
    return getComputedStyle(this).getPropertyValue(name).trim();
  }

  private _isDarkMode(): boolean {
    if (this.theme === "dark") return true;
    if (this.theme === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  private _isMobile(): boolean {
    return window.innerWidth < 700;
  }

  private _gridColor(): string {
    const isDark = this._isDarkMode();
    const textSecondary = this._getThemeValue("--bab-text-secondary") || (isDark ? "#94a3b8" : "#6b7280");
    return withOpacity(textSecondary, isDark ? 0.4 : 0.15);
  }

  private _toggleExpand() {
    this._isExpanded = !this._isExpanded;
    if (this._isExpanded) {
      this.setAttribute("expanded", "");
      document.body.style.overflow = "hidden";
      // Give Lit a chance to render the overlay canvas
      setTimeout(() => this._initOverlayChart(), 0);
    } else {
      this.removeAttribute("expanded");
      document.body.style.overflow = "";
      this._overlayChart?.destroy();
      this._overlayChart = null;
    }
    this.dispatchEvent(new CustomEvent("toggle-expand", {
      detail: { expanded: this._isExpanded },
      bubbles: true,
      composed: true
    }));
  }

  private _initOverlayChart() {
    if (!this._overlayCanvas || this._overlayChart) return;
    this._overlayChart = this._createChart(this._overlayCanvas);
  }

  private _createChart(canvas: HTMLCanvasElement): Chart {
    const isDark = this._isDarkMode();
    const isMobile = this._isMobile();
    const gridColor = this._gridColor();
    const textColor = this._getThemeValue("--bab-text") || (isDark ? "#ffffff" : "#000000");

    const isOverlay = canvas === this._overlayCanvas;
    
    const legendFontSize = isOverlay ? (isMobile ? 12 : 14) : (isMobile ? 10 : 12);
    const labelFontSize = isOverlay ? (isMobile ? 14 : 16) : (isMobile ? 11 : 12);

    return new Chart(canvas, {
      type: "radar",
      data: {
        labels: this._labels(),
        datasets: this._datasets(),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500 },
        layout: { padding: isOverlay ? 40 : 10 },
        plugins: {
          legend: { 
            display: this.datasets.length > 1,
            position: "bottom",
            labels: { 
              color: textColor,
              usePointStyle: true,
              padding: 20,
              font: { size: legendFontSize }
            }
          },
          tooltip: {
            callbacks: {
              title: (items) => {
                if (!items.length) return "";
                const idx = items[0].dataIndex;
                return (items[0].chart.data.labels?.[idx] as string) ?? "";
              },
              label: (ctx) => {
                const val = ctx.raw as number | null;
                const setLabel = ctx.dataset.label || "";
                if (val === null) return `${setLabel}: Not rated`;
                const ratingLabel = this.config.defaultLabels?.[val] ?? String(val);
                return `${setLabel}: ${val} — ${ratingLabel}`;
              },
            },
          },
        },
        scales: {
          r: {
            min: this.config.versions[this._activeVersionKey()]?.minRating ?? 0,
            max:
              this.config.versions[this._activeVersionKey()]?.maxRating ?? 5,
            ticks: { 
              stepSize: this.config.versions[this._activeVersionKey()]?.labelInterval ?? 1,
              display: false 
            },
            grid: { color: gridColor },
            angleLines: { color: gridColor },
            pointLabels: { 
              font: { 
                size: labelFontSize, 
                weight: "bold" 
              },
              color: textColor 
            },
          },
        },
      },
    });
  }

  private _initChart() {
    if (this._chart) return;
    this._chart = this._createChart(this._canvas);
  }

  private _updateChartInstance(chart: Chart | null) {
    if (!chart) return;

    const isDark = this._isDarkMode();
    const isMobile = this._isMobile();
    const gridColor = this._gridColor();
    const textColor = this._getThemeValue("--bab-text") || (isDark ? "#ffffff" : "#000000");

    const isOverlay = chart.canvas === this._overlayCanvas;

    const legendFontSize = isOverlay ? (isMobile ? 12 : 14) : (isMobile ? 10 : 12);
    const labelFontSize = isOverlay ? (isMobile ? 14 : 16) : (isMobile ? 11 : 12);

    chart.data.labels = this._labels();
    chart.data.datasets = this._datasets();

    const rScale = chart.options.scales?.r as RadialScaleConfig | undefined;
    if (rScale) {
      rScale.min = this.config.versions[this._activeVersionKey()]?.minRating ?? 0;
      rScale.max = this.config.versions[this._activeVersionKey()]?.maxRating ?? 5;
      rScale.ticks.stepSize = this.config.versions[this._activeVersionKey()]?.labelInterval ?? 1;
      rScale.grid.color = gridColor;
      rScale.angleLines.color = gridColor;
      rScale.pointLabels.color = textColor;
      rScale.pointLabels.font.size = labelFontSize;
    }

    if (chart.options.plugins?.legend?.labels) {
      chart.options.plugins.legend.display = this.datasets.length > 1;
      chart.options.plugins.legend.labels.color = textColor;
      chart.options.plugins.legend.labels.usePointStyle = true;
      
      const font = chart.options.plugins.legend.labels.font as { size?: number };
      if (font) {
        font.size = legendFontSize;
      }
    }

    chart.update();
  }

  private _updateChart() {
    this._updateChartInstance(this._chart);
    this._updateChartInstance(this._overlayChart);
  }

  private _requestUpdate() {
    if (this._updateRequested) return;
    this._updateRequested = true;
    requestAnimationFrame(() => {
      this._updateChart();
      this._updateRequested = false;
    });
  }

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has("datasets")) {
      this._isEmpty = !this.datasets.some(set => 
        Object.values(set.ratings).some(v => v !== null)
      );
    }
  }

  firstUpdated() {
    this._initChart();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("config") || changed.has("datasets") || changed.has("theme") || changed.has("activeVersion")) {
      this._requestUpdate();
    }
  }

  private _query = window.matchMedia("(prefers-color-scheme: dark)");
  private _onThemeChange = () => {
    if (this.theme === "system") this._requestUpdate();
  };

  connectedCallback() {
    super.connectedCallback();
    this._query.addEventListener("change", this._onThemeChange);
    window.addEventListener("keydown", this._onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._isExpanded) {
      document.body.style.overflow = "";
    }
    this._query.removeEventListener("change", this._onThemeChange);
    window.removeEventListener("keydown", this._onKeyDown);
    this._chart?.destroy();
    this._overlayChart?.destroy();
    this._chart = null;
    this._overlayChart = null;
  }

  /**
   * Returns a Base64 data URL of the current chart, including metadata.
   * @param type - Image format (default: 'image/png').
   * @returns The data URL string.
   */
  public getImageDataUrl(type = "image/png"): string {
    const tempCanvas = document.createElement("canvas");
    const ctx = tempCanvas.getContext("2d")!;
    const padding = 40;
    const headerHeight = 80;
    const footerHeight = 40;

    tempCanvas.width = this._canvas.width;
    tempCanvas.height = this._canvas.height + headerHeight + footerHeight;

    const isDark = this._isDarkMode();
    ctx.fillStyle = isDark ? "#1f2937" : "#ffffff";
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    ctx.drawImage(this._canvas, 0, headerHeight);

    const textColor = isDark ? "#ffffff" : "#000000";
    ctx.fillStyle = textColor;
    ctx.textAlign = "center";
    
    const title = this._getChartTitle();

    let fontSize = 24;
    ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
    while (ctx.measureText(title).width > tempCanvas.width - 2 * padding && fontSize > 12) {
      fontSize -= 2;
      ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
    }
    
    ctx.fillText(title, tempCanvas.width / 2, padding + (24 - fontSize) / 2 + 10);

    ctx.font = "14px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = isDark ? "#9ca3af" : "#6b7280";
    const dateStr = new Date().toLocaleDateString();
    ctx.fillText(`Generated on ${dateStr}`, tempCanvas.width / 2, tempCanvas.height - 20);

    return tempCanvas.toDataURL(type);
  }

  /**
   * Returns a data URL for an SVG version of the chart.
   * @returns The data URL string.
   */
  public getSvgDataUrl(): string {
    const width = this._canvas.width;
    const height = this._canvas.height;
    const svgContext = new C2S(width, height);
    
    // Create a temporary chart using the SVG context
    const tempChart = new Chart(svgContext as unknown as HTMLCanvasElement, {
      type: "radar",
      data: {
        labels: this._labels(),
        datasets: this._datasets(),
      },
      options: {
        ...this._chart?.options,
        animation: false,
        responsive: false,
      } as ChartOptions<"radar">,
    });

    const svgString = svgContext.getSerializedSvg();
    tempChart.destroy();
    
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);
  }

  render() {
    return html`
      <div class="chart-title">${this._getChartTitle()}</div>
      <button class="expand-btn" @click="${this._toggleExpand}" aria-label="Expand chart">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 3 21 3 21 9"></polyline>
          <polyline points="9 21 3 21 3 15"></polyline>
          <line x1="21" y1="3" x2="14" y2="10"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        </svg>
      </button>
      <div class="chart-container">
        <canvas></canvas>
        ${this._isEmpty ? html`<div class="watermark">Rate some ${this.config.attributeTermPlural || "skills"} to see your chart</div>` : ""}
      </div>

      <div class="overlay ${this._isExpanded ? "overlay--visible" : ""}" @click="${this._toggleExpand}">
        <div class="overlay-content" @click="${(e: Event) => e.stopPropagation()}">
          <button class="close-btn" @click="${this._toggleExpand}" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="chart-title">${this._getChartTitle()}</div>
          <canvas></canvas>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-spider-chart": BabSpiderChart;
  }
}
